import { useState } from 'react';

import { useScenariosStore } from '@/entities/scenario/scenarios.store';
import { useApi } from '@/shared/api/hooks/useApi';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { getRelatedToAssetDtosIds } from '@/shared/lib/helpers/scenario';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { log } from '@/shared/utils/log';

import { useNotices } from '../notices/useNotices';

import { ScenarioRequestData } from './types/scenarioRequest';
import { BeneficiariesDataMap, getBeneficiariesDataMap } from './utils/getBeneficiariesDataMap';
import { getScenarioRequestData } from './utils/getScenarioRequestData';
import { updateScenarioCollectionItemsPublicKeys } from './utils/updateScenarioCollectionItemsPublicKeys';
import { useEditScenarioContext } from './editScenarioContext';
import { isScenarioFormDataFull } from './helpers';

export const mergeMaps = (maps: Map<CollectionName, string[]>[]): Map<CollectionName, string[]> => {
  const result = new Map<CollectionName, string[]>();

  for (const map of maps) {
    for (const [key, values] of map.entries()) {
      if (!result.has(key)) {
        result.set(key, [...values]);
      } else {
        const existingValues = result.get(key)!;
        const uniqValues = Array.from(new Set(existingValues.concat(values)));
        result.set(key, uniqValues);
      }
    }
  }

  return result;
};

type EditScenarioPops = {
  requestData: ScenarioRequestData;
  scenarioName: string;
  relatedDtoIdsMap: Map<CollectionName, string[]>;
  beneficiariesDataMap: BeneficiariesDataMap;
};

export const useSubmitScenario = () => {
  const { t } = useTranslation('features/editScenarios');
  const router = useAppRouter();
  const { scenario, isEdit, scenarioDbId, scenarioDtoId } = useEditScenarioContext();
  const sjDb = useSJDatabase();
  const api = useApi();
  const { keyPair } = useKeyPairStore();
  const [isLoading, setIsLoading] = useState(false);
  const notices = useNotices();
  const { addScenario: addScenarioToStore, updateScenario: updateScenarioStore } =
    useScenariosStore();

  const onError = (error?: string) => {
    const prefix = isEdit ? '[editScenario]' : '[createScenario]';
    log.error(`${prefix} ${error}`);

    if (error) {
      const errorMessage = isEdit ? 'Edit scenario failed' : 'Create scenario failed';

      notices.antd.api.error({ message: errorMessage, description: error });
    }

    setIsLoading(false);
  };

  const createScenario = async ({
    requestData,
    scenarioName,
    beneficiariesDataMap,
    relatedDtoIdsMap,
  }: EditScenarioPops) => {
    if (!keyPair) {
      onError();
      return;
    }

    const response = await api.scenarioCreate(requestData);

    if (response.error || !response.data) {
      onError(response.error?.message);
      return;
    }

    const createdScenarioId = response.data.id;

    const createdScenarioMeta = await sjDb.collections.scenarioMetaInfos?.create(
      {
        id: sjDb.generateId(CollectionName.SCENARIO_META_INFO),
        centralDbId: createdScenarioId,
        name: scenarioName,
      },
      [keyPair.publicKey],
    );

    await api.scenarioUpdateCollectionItemId(createdScenarioId, {
      collectionItemId: createdScenarioMeta?.id,
    });

    await updateScenarioCollectionItemsPublicKeys({
      relatedDtoIdsMap: relatedDtoIdsMap,
      beneficiariesDataMap,
      sjDb,
    });

    addScenarioToStore(response.data);
    await router.push('/scenarios');
    notices.success(t('notices.transferCreated'));
    setIsLoading(false);
  };

  const editScenario = async ({
    requestData,
    scenarioName,
    beneficiariesDataMap,
    relatedDtoIdsMap,
  }: EditScenarioPops) => {
    if (!scenarioDbId || !scenarioDtoId) {
      onError('Scenario ID missing');
      return;
    }

    const response = await api.scenarioEdit(scenarioDbId, requestData);

    if (response.error || !response.data) {
      onError(response.error?.message);
      return;
    }

    await sjDb.collections.scenarioMetaInfos?.update({
      id: scenarioDtoId,
      centralDbId: scenarioDbId,
      name: scenarioName,
    });

    await updateScenarioCollectionItemsPublicKeys({
      relatedDtoIdsMap: relatedDtoIdsMap,
      beneficiariesDataMap,
      sjDb,
    });

    updateScenarioStore(response.data);

    await router.push('/scenarios');

    notices.success(t('notices.transferUpdated'));

    setIsLoading(false);
  };

  const onSubmit = async () => {
    setIsLoading(true);

    if (!isScenarioFormDataFull(scenario)) {
      onError();
      return;
    }

    const beneficiariesResponse = await api.profilesGetBeneficiaries();
    const beneficiariesData = beneficiariesResponse.data ?? [];

    if (!beneficiariesData) {
      onError(beneficiariesResponse.error?.message);
      return;
    }

    const beneficiariesCollection = sjDb.collections.beneficiaries;

    if (!beneficiariesCollection) {
      onError();
      return;
    }

    const beneficiariesDataMap = getBeneficiariesDataMap({
      beneficiariesData: beneficiariesData,
      beneficiariesCollection,
      step2: scenario.step2,
    });

    const assetsDtoIds = scenario.step1.assetsDtoIds;

    const relatedDtoIdsMapList = assetsDtoIds.map((assetDtoId) =>
      getRelatedToAssetDtosIds({
        assetDtoId,
        sjDb,
        includeAsset: true,
      }),
    );

    const relatedDtoIdsMap = mergeMaps(relatedDtoIdsMapList);

    const { data: requestData, error } = getScenarioRequestData({
      scenario,
      beneficiariesDataMap,
      relatedDtoIdsMap,
    });

    if (error || !requestData) {
      onError(error);
      return;
    }

    const scenarioName = scenario.step1.scenarioName;

    if (isEdit) {
      await editScenario({
        beneficiariesDataMap,
        relatedDtoIdsMap,
        requestData,
        scenarioName,
      });
    } else {
      await createScenario({
        beneficiariesDataMap,
        relatedDtoIdsMap,
        requestData,
        scenarioName,
      });
    }
  };

  return { onSubmit, loading: isLoading };
};
